import { Link } from '@tanstack/react-router';

import type { App } from '@keboola/api-client/developerPortal/types';
import { Button, Card } from '@keboola/design';

import { useAdminAppForWaves } from '../queries';
import { describeWave, readPins, resolveWave, shownWaves } from '../waves';

import { EditWavePinsModal } from './EditWavePinsModal';

/**
 * What each rollout wave runs, from the component's `waveTags`.
 *
 * - A pinned wave is checked against apps-api's `?wave=N` answer, which ignores a pin that no
 *   longer matches the current image; such a wave runs the latest version.
 */
export function RolloutWavesCard({ app }: { app: App }) {
  const pins = readPins(app.waveTags);
  const waves = shownWaves(pins);
  const pinnedWaves = waves.filter((wave) => resolveWave(pins, wave) !== null);
  const asStack = useAdminAppForWaves(app.id, pinnedWaves);

  const isIgnored = (wave: number) => {
    const served = asStack[pinnedWaves.indexOf(wave)]?.data;
    return served !== undefined && served.version === app.version && served.rolloutWave === null;
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title as="h2">Rollout waves</Card.Title>
      </Card.Header>
      <Card.Content className="tw:flex tw:flex-col tw:gap-2 tw:text-sm">
        {waves.map((wave) => (
          <div key={wave} className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-2">
            <span className="tw:text-muted-foreground">Wave {wave}</span>
            <span>{describeWave(pins, wave, isIgnored(wave))}</span>
          </div>
        ))}
        <div className="tw:flex tw:flex-wrap tw:gap-2 tw:pt-2">
          <EditWavePinsModal app={app} />
          <Button asChild variant="inline" size="small">
            <Link to="/admin/components/$appId/versions" params={{ appId: app.id }}>
              Versions and revert
            </Link>
          </Button>
        </div>
      </Card.Content>
    </Card>
  );
}
